<template>
  <custom-remote-el-select
    v-model="current"
    ref="customRemoteElSelect"
    remote
    filterable
    reserve-keyword
    :placeholder="placeholder || (placeholderI18nKey && $t(placeholderI18nKey))"
    :default-first-option="defaultFirstOption"
    :allow-create="allowCreate"
    :loading="loading"
    :multiple="multiple"
    :clearable="clearable"
    :disabled="disabled"
    :noDataText="noDataText"
    :remote-method="searchOptionDebounce"
    @visible-change="onVisibleChange"
  >
    <template v-if="groupedOption">
      <el-option-group
        v-for="(group, groupIndex) in options"
        :key="groupIndex"
        :label="group[groupLabelKey || 'label']"
      >
        <el-option
          v-for="(option) in (group[groupOptionKey || 'children'])"
          :key="getOptionKey(option)"
          :label="getOptionLabel(option)"
          :value="getOptionKey(option)"
        >
          <span v-auto-tooltip class="option-label">{{ getOptionLabel(option) }}</span>
        </el-option>
      </el-option-group>
    </template>
    <template v-else>
      <el-option
        v-for="(option) in options"
        :key="getOptionKey(option)"
        :label="getOptionLabel(option)"
        :value="getOptionKey(option)"
      >
        <span v-auto-tooltip class="option-label">{{ getOptionLabel(option) }}</span>
      </el-option>
    </template>
  </custom-remote-el-select>
</template>

<script>
import _ from 'lodash'

export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      required: false
    },
    loader: {
      type: Function,
      required: true
    },
    pageSize: {
      type: Number,
      default: 20
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'id'
    },
    searchKey: {
      type: String,
      default: 'searchTextList'
    },
    groupedOption: {
      type: Boolean,
      default: false
    },
    groupLabelKey: {
      type: String,
      default: 'label'
    },
    groupOptionKey: {
      type: String,
      default: 'children'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false
    },
    placeholderI18nKey: {
      type: String,
      required: false
    },
    filterMethod: {
      type: Function,
      default: null
    },
    allowCreate: {
      type: Boolean,
      default: false
    },
    defaultFirstOption: {
      type: Boolean,
      default: false
    },
    onlyFilter: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loaderPromise: null,
      optionList: [],
      defaultOptions: [],
      loading: false,
      hasLoaded: false,
      hasSetDefault: false,
      searchQuery: null,
      searchedOptions: [],
      page: 1
    }
  },
  computed: {
    current: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('change', val)
      }
    },
    options () {
      if (this.hasSetDefault) {
        return this.defaultOptions
      } else if (this.hasLoaded) {
        const pageSize = this.pageSize || 20
        const optionList = this.searchQuery ? this.searchedOptions : (this.onlyFilter ? [] : this.optionList)
        return _.slice(optionList, 0, this.page * pageSize)
      }
      return []
    },
    noDataText () {
      if (this.loading) {
        return this.$t('el.select.loading')
      }
      if (!this.optionList || this.optionList.length < 1) {
        return this.$t('el.select.noData')
      }
      if (_.isEmpty(this.options)) {
        if (this.searchQuery) {
          return this.$t('el.select.noMatch')
        }
      }
      return this.$t('admin.application.applicantList.label.pleaseStartTyping')
    }
  },
  created () {
    this.loadNextPageOptions = _.debounce(() => {
      if (this.page * this.pageSize < this.optionList.length) {
        this.page += 1
      }
    }, 100, {
      leading: true,
      trailing: false
    })
    this.searchOptionDebounce = _.debounce(this.searchOption, 500)
  },
  methods: {
    getOptionLabel (option) {
      return _.get(option, this.labelKey || 'label')
    },
    getOptionKey (option) {
      return _.get(option, this.valueKey || 'id')
    },
    loadOptionList () {
      if (this.hasLoaded) {
        return
      }
      if (this.loaderPromise) {
        return this.loaderPromise
      }
      this.loaderPromise = Promise.resolve(this.loader()).then(options => {
        this.optionList = options
        return this.$nextTick()
      }).then(() => {
        this.hasLoaded = true
      })
      return this.loaderPromise
    },
    async setDefaultValue (val, valKey) {
      if (this.multiple && !val?.length) {
        this.current = []
        return
      } else if (val === null || val === undefined) {
        this.current = null
        return
      }
      this.hasSetDefault = true
      this.loading = true
      this.setDefaultValuePromise = Promise.resolve(this.loadOptionList()).then(() => {
        if (!valKey) {
          valKey = this.valueKey || 'id'
        }
        const optionList = this.optionList
        if (this.multiple) {
          this.defaultOptions = _.filter(optionList, (option) => val.includes(option[valKey]))
          const defaultValues = _.map(this.defaultOptions, valKey)
          const createValues = _.difference(val, defaultValues)
          val = _.map(this.defaultOptions, this.getOptionKey)
          if (this.allowCreate) {
            val.push(...createValues)
          }
        } else {
          this.defaultOptions = _.filter(optionList, { [valKey]: val })
          val = this.getOptionKey(this.defaultOptions[0])
        }
        return this.$nextTick()
      }).then(() => {
        this.current = val
      }).finally(() => {
        this.loading = false
      })
    },
    async onVisibleChange (visible) {
      if (visible) {
        if (this.hasSetDefault) {
          await this.setDefaultValuePromise
          this.hasSetDefault = false
        }
        if (!this.hasLoaded) {
          this.loading = true
          this.loadOptionList().finally(() => {
            this.loading = false
          })
        }
        this.initScroll()
      } else {
        this.searchQuery = null
        this.searchedOptions = []
      }
    },
    async initScroll () {
      await this.loadOptionList()
      await this.$nextTick()
      const el = this.$children[0].popperElm.querySelector('.el-select-dropdown__wrap')
      if (el && !el.__option_scroll_listener__) {
        el.__option_scroll_listener__ = this.onOptionScroll
        el.addEventListener('scroll', el.__option_scroll_listener__)
      }
    },
    onOptionScroll (e) {
      const el = e.currentTarget
      const bottomLength = el.scrollHeight - el.scrollTop
      const safeLength = 20
      if (bottomLength - safeLength <= el.clientHeight) {
        this.loadNextPageOptions()
      }
    },
    defaultFilterMethod (lowerQuery, option, index, optionList) {
      let searchTextList
      if (this.searchKey) {
        searchTextList = option[this.searchKey]
      } else {
        searchTextList = option.searchTextList
      }
      if (!searchTextList) {
        searchTextList = [this.getOptionLabel(option)]
      }
      if (!_.isArray(searchTextList)) {
        searchTextList = [searchTextList]
      }
      return _.chain(searchTextList)
        .map(_.toLower)
        .some(text => text.includes(lowerQuery) || text.includes(lowerQuery))
        .value()
    },
    async searchOption (query) {
      this.loading = true
      try {
        this.searchQuery = query
        if (!query) {
          return
        }
        const lowerQuery = _.toLower(query)
        await this.loadOptionList()
        this.searchedOptions = _.filter(this.optionList, (option, index) => {
          if (this.filterMethod) {
            return this.filterMethod(lowerQuery, option, index, this.optionList)
          } else {
            return this.defaultFilterMethod(lowerQuery, option, index, this.optionList)
          }
        })
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        if (!this.value) {
          this.$emit('change', null)
        } else {
          this.selectedItems = this.value
        }
      }
    },
    '$i18n.locale' () {
      this.$nextTick(() => {
        if (this.current?.length) {
          this.$refs.customRemoteElSelect.currentPlaceholder = ''
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
